// Lightweight SSO hook: eviq.pro can redirect to this app with
// `?token=<HS256-jwt>` (preferred — signed & verified against backend) or
// `?member=NNNN&name=Whoever` (fallback / preview only).
//
// Once the JWT is verified, the resolved `{ number, name }` is persisted in
// localStorage so subsequent navigations retain the identity without the
// query param. The token itself is discarded after verification.

const MEMBER_KEY = "eviq_member_override";
const VERIFIED_KEY = "eviq_member_verified";
import { api, endpoints } from "@/lib/api";

// New endpoint keys — added here to avoid circular imports
if (!endpoints.ssoVerify) endpoints.ssoVerify = () => "/auth/sso/verify";

export const readMemberFromUrl = () => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("member") || url.searchParams.get("m");
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 9999) return null;
    const name = url.searchParams.get("name") || null;
    // Persist so subsequent navigations keep the identity without the query param.
    try {
        window.localStorage.setItem(
            MEMBER_KEY,
            JSON.stringify({ number: n, name, at: Date.now(), verified: false })
        );
        window.localStorage.removeItem(VERIFIED_KEY);
    } catch {}
    // Clean the URL so shared screenshots don't leak the member number.
    url.searchParams.delete("member");
    url.searchParams.delete("m");
    url.searchParams.delete("name");
    window.history.replaceState({}, "", url.toString());
    return { number: n, name };
};

// Verified SSO flow: read ?token=<jwt>, POST to backend, persist on success.
// Returns a promise that resolves to { number, name, verified: true } on
// success or null if no token / verification failed.
export const verifySsoToken = async () => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    if (!token) return null;
    try {
        const r = await api.post(endpoints.ssoVerify(), { token });
        const { member_number, display_name } = r.data || {};
        if (!member_number) return null;
        const record = {
            number: member_number,
            name: display_name || null,
            at: Date.now(),
            verified: true,
        };
        try {
            window.localStorage.setItem(MEMBER_KEY, JSON.stringify(record));
            window.localStorage.setItem(VERIFIED_KEY, "1");
        } catch {}
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
        return record;
    } catch (e) {
        // Silently fall through — this app should still show the demo view
        // rather than blocking the page for an SSO issue.
        console.warn("[SSO] token verification failed:", e?.response?.data?.detail || e?.message);
        return null;
    }
};

export const getStoredMember = () => {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(MEMBER_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const isVerified = () => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(VERIFIED_KEY) === "1";
};

export const clearStoredMember = () => {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.removeItem(MEMBER_KEY);
        window.localStorage.removeItem(VERIFIED_KEY);
    } catch {}
};

// Returns the user_id string that /api/user/impact expects.
// Precedence: URL member param → localStorage → "demo-user".
// (For SSO-token flow, call verifySsoToken() at app boot to populate
// localStorage first, then resolveUserId() picks it up.)
export const resolveUserId = () => {
    const url = readMemberFromUrl();
    const stored = url || getStoredMember();
    if (stored?.number) return `member-${String(stored.number).padStart(4, "0")}`;
    return "demo-user";
};
